import React, { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  MoreHorizontal,
  FileText,
  Sparkles,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { trpcClient } from '@/lib/trpc';
import { formatDate, cn } from '@/lib/utils';
import { PageFilterTags, type PageFilterTagItem } from '@/components/shared/PageFilterTags';
import { ListPageShell } from '@/components/shared/ListPageShell';
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from '@/components/shared/DataTableRow';
import { BulkActionBar, type BulkAction } from '@/components/shared/BulkActionBar';
import { useRowSelection } from '@/hooks/useRowSelection';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useConfirm } from '@/providers/ConfirmProvider';
import { executeBulkAction } from '@/utils/bulkActionUtils';
import { exportToExcel as exportRowsToExcel, type ExcelColumnDef } from '@/lib/excel.utils';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { useExcelExport } from '@/hooks/useExcelExport';
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

const STATUS_ITEMS: PageFilterTagItem[] = [
  { key: 'PENDING', label: 'Chờ xử lý', tone: 'amber' },
  { key: 'DESIGNING', label: 'Đang thiết kế', tone: 'blue' },
  { key: 'REVIEWING', label: 'Chờ duyệt', tone: 'amber' },
  { key: 'APPROVED', label: 'Đã duyệt', tone: 'emerald' },
  { key: 'REJECTED', label: 'Từ chối', tone: 'rose' },
];

export default function DesignList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSizeState(25);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'my'>('all');

  // Single Source of Search từ menu header
  const { headerSearch: searchTerm } = useHeaderSearch();

  // Query stats để đếm số lượng bản ghi theo trạng thái
  const { data: statsData } = useQuery({
    queryKey: ['designs', 'stats'],
    queryFn: async () => (trpcClient as any).designs.stats.query(),
  });

  // Query danh sách từ server side với search, status, page, limit
  const { data, isLoading } = useQuery({
    queryKey: ['designs', 'list', currentPage, pageSize, searchTerm, selectedStatus, activeTab],
    queryFn: async () =>
      (trpcClient as any).designs.list.query({
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        status: (selectedStatus as any) || undefined,
      }),
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  // Cập nhật số đếm vào STATUS_ITEMS
  const filterTagsWithCount = useMemo(() => {
    return STATUS_ITEMS.map((item) => ({
      ...item,
      count: statsData ? (statsData[item.key] || 0) : undefined,
    }));
  }, [statsData]);

  // Xóa yêu cầu thiết kế
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (trpcClient as any).designs.delete.mutate({ id }),
    onSuccess: () => {
      toast.success('Đã xóa yêu cầu thiết kế');
      queryClient.invalidateQueries({ queryKey: ['designs'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Không thể xóa');
    },
  });

  // Xuất Excel chuẩn (Full Server-Side Data)
  const mapDesignExportRow = (i: any) => ({
    id: i.id,
    productCode: i.product?.productCode || '',
    productName: i.product?.productName || '',
    customerName: i.product?.customer?.name || '',
    designType: i.designType === 'NEW' ? 'Thiết kế mới' : 'Chỉnh sửa',
    status: i.status,
    assigneeName: i.assignee?.fullName || '',
    createdAt: formatDate(i.createdAt),
  });

  const { exportToExcel } = useExcelExport({
    filename: 'Danh_sach_yeu_cau_thiet_ke',
    sheetName: 'ThietKe',
    columns: [
      { header: 'Mã Yêu Cầu', label: 'Mã Yêu Cầu', key: 'id', width: 22 },
      { header: 'Mã Sản Phẩm', label: 'Mã Sản Phẩm', key: 'productCode', width: 18 },
      { header: 'Tên Sản Phẩm', label: 'Tên Sản Phẩm', key: 'productName', width: 30 },
      { header: 'Khách Hàng', label: 'Khách Hàng', key: 'customerName', width: 25 },
      { header: 'Loại Thiết Kế', label: 'Loại Thiết Kế', key: 'designType', width: 16 },
      { header: 'Trạng Thái', label: 'Trạng Thái', key: 'status', width: 16 },
      { header: 'Người Phụ Trách', label: 'Người Phụ Trách', key: 'assigneeName', width: 20 },
      { header: 'Ngày Tạo', label: 'Ngày Tạo', key: 'createdAt', width: 16 },
    ],
    queryFn: (params) =>
      (trpcClient as any).designs.list.query({
        ...params,
        search: searchTerm || undefined,
        status: (selectedStatus as any) || undefined,
      }),
    currentFilters: {
      search: searchTerm || undefined,
      status: (selectedStatus as any) || undefined,
    },
    transformData: (rows) => (rows || []).map(mapDesignExportRow),
    localData: items.map(mapDesignExportRow),
  });

  // ── Chọn nhiều dòng + thao tác hàng loạt ──
  const confirmDialog = useConfirm();
  const { hasPermission } = useRoleAccess();
  const rowIds = useMemo(() => items.map((i: any) => i.id), [items]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const SELECTED_EXPORT_COLUMNS: ExcelColumnDef[] = [
    { key: 'id', label: 'Mã Yêu Cầu', width: 22 },
    { key: 'productCode', label: 'Mã Sản Phẩm', width: 18 },
    { key: 'productName', label: 'Tên Sản Phẩm', width: 30 },
    { key: 'customerName', label: 'Khách Hàng', width: 25 },
    { key: 'designType', label: 'Loại Thiết Kế', width: 16 },
    { key: 'status', label: 'Trạng Thái', width: 16 },
    { key: 'assigneeName', label: 'Người Phụ Trách', width: 20 },
    { key: 'createdAt', label: 'Ngày Tạo', width: 16 },
  ];
  const bulkActions: BulkAction[] = selectedCount
    ? [
        {
          icon: <Download className="h-4 w-4" />,
          label: 'Xuất Excel',
          variant: 'outline',
          onClick: () =>
            exportRowsToExcel(
              items.filter((i: any) => selectedIds.has(i.id)).map(mapDesignExportRow),
              SELECTED_EXPORT_COLUMNS,
              'yeu_cau_thiet_ke_da_chon',
            ),
        },
        ...(hasPermission('specs', 'delete')
          ? [
              {
                icon: <Trash2 className="h-4 w-4" />,
                label: `Xoá (${selectedCount})`,
                variant: 'destructive' as const,
                onClick: () =>
                  executeBulkAction({
                    confirmFn: confirmDialog,
                    selectedIds: Array.from(selectedIds),
                    actionFn: async (id) => (trpcClient as any).designs.delete.mutate({ id }),
                    actionName: 'xóa',
                    itemType: 'yêu cầu thiết kế',
                    onSuccess: () => {
                      clearSelection();
                      queryClient.invalidateQueries({ queryKey: ['designs'] });
                    },
                    requireConfirm: true,
                    confirmMessage: `Xóa ${selectedCount} yêu cầu thiết kế đã chọn?`,
                  }),
              },
            ]
          : []),
      ]
    : [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-50 font-medium">Đã duyệt</Badge>;
      case 'DESIGNING':
        return <Badge className="bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-50 font-medium">Đang thiết kế</Badge>;
      case 'REVIEWING':
        return <Badge className="bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50 font-medium">Chờ duyệt</Badge>;
      case 'REJECTED':
        return <Badge className="bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-50 font-medium">Từ chối</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100 font-medium">Chờ xử lý</Badge>;
    }
  };

  return (
    <ListPageShell
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={total}
      onPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      onExport={exportToExcel}
      /* HÀNG 1: Flat Underline Tabs (Trái) & Action Buttons (Phải) */
      tabs={
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => { setActiveTab('all'); setCurrentPage(1); }}
            className={emsTabClass(activeTab === 'all', { size: "sm" })}
          >
            Tất cả hồ sơ ({statsData?.TOTAL || total})
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('my'); setCurrentPage(1); }}
            className={emsTabClass(activeTab === 'my', { size: "sm" })}
          >
            Của tôi
          </button>
        </div>
      }
      actions={
        <Button
          size="sm"
          className="h-7 px-2.5 text-xs bg-primary text-primary-foreground font-medium gap-1.5 shadow-2xs cursor-pointer"
          onClick={() => navigate({ to: '/designs/create' as any })}
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Tạo Yêu Cầu</span>
        </Button>
      }
      /* HÀNG 2: Semantic Filter Chips */
      headerFilters={
        <PageFilterTags
          items={filterTagsWithCount}
          selectedKeys={selectedStatus ? [selectedStatus] : null}
          onSelect={(key) => {
            setSelectedStatus(key);
            setCurrentPage(1);
          }}
        />
      }
    >
      <Table className={rowSelection.selectionMode ? 'apv-select-mode' : undefined}>
        <TableHeader>
          <TableRow className="bg-slate-50 border-b border-slate-200">
            <DataTableSelectAllCheckbox rowSelection={rowSelection} />
            <TableHead className="w-[120px] text-xs font-semibold text-slate-700 whitespace-nowrap">Mã YC</TableHead>
            <TableHead className="min-w-[200px] text-xs font-semibold text-slate-700 whitespace-nowrap">Sản Phẩm</TableHead>
            <TableHead className="w-[180px] text-xs font-semibold text-slate-700 whitespace-nowrap">Khách Hàng</TableHead>
            <TableHead className="w-[110px] text-xs font-semibold text-slate-700 whitespace-nowrap">Phân Loại</TableHead>
            <TableHead className="w-[120px] text-xs font-semibold text-slate-700 whitespace-nowrap">Trạng Thái</TableHead>
            <TableHead className="w-[140px] text-xs font-semibold text-slate-700 whitespace-nowrap">Phụ Trách</TableHead>
            <TableHead className="w-[110px] text-xs font-semibold text-slate-700 whitespace-nowrap">Ngày Tạo</TableHead>
            <TableHead className="w-[60px] text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Thao Tác</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="h-32 text-center text-xs text-slate-400">
                {isLoading ? 'Đang tải dữ liệu hồ sơ thiết kế...' : 'Không có yêu cầu thiết kế nào.'}
              </TableCell>
            </TableRow>
          ) : (
            items.map((item: any) => (
              <DataTableRow
                key={item.id}
                id={item.id}
                rowSelection={rowSelection}
                className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                onClick={() => navigate({ to: `/designs/${item.id}` as any })}
              >
                <DataTableRowCheckbox id={item.id} rowSelection={rowSelection} />
                {/* Mã YC */}
                <TableCell className="text-xs font-mono font-medium text-slate-800 whitespace-nowrap">
                  {item.id.slice(-8).toUpperCase()}
                </TableCell>

                {/* Sản phẩm */}
                <TableCell className="text-xs whitespace-nowrap">
                  {item.product ? (
                    <div className="leading-tight">
                      <div className="font-semibold text-slate-800">{item.product.productCode}</div>
                      {item.product.productName && (
                        <div className="text-[11px] text-slate-500 truncate max-w-[260px]">{item.product.productName}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400">---</span>
                  )}
                </TableCell>

                {/* Khách hàng */}
                <TableCell className="text-xs whitespace-nowrap text-slate-700">
                  {item.product?.customer?.name || item.product?.customer?.code || '---'}
                </TableCell>

                {/* Loại thiết kế */}
                <TableCell className="text-xs whitespace-nowrap">
                  {item.designType === 'NEW' ? 'Thiết kế mới' : 'Chỉnh sửa'}
                </TableCell>

                {/* Trạng thái */}
                <TableCell className="text-xs whitespace-nowrap">
                  {getStatusBadge(item.status)}
                </TableCell>

                {/* Phụ trách */}
                <TableCell className="text-xs text-slate-700 whitespace-nowrap">
                  {item.assignee?.fullName || '---'}
                </TableCell>

                {/* Ngày tạo */}
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                  {formatDate(item.createdAt)}
                </TableCell>

                {/* Thao tác */}
                <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 cursor-pointer">
                        <MoreHorizontal className="w-4 h-4 text-slate-500" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          if (confirm('Bạn có chắc chắn muốn xóa yêu cầu thiết kế này?')) {
                            deleteMutation.mutate(item.id);
                          }
                        }}
                        className="text-xs text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Xóa
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </DataTableRow>
            ))
          )}
        </TableBody>
      </Table>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />
    </ListPageShell>
  );
}
